/**
 * Carto Positron basemap for Leaflet.
 *
 * Default is Carto's documented vector style (MapLibre GL) plus the official
 * @maplibre/maplibre-gl-leaflet bridge, so Leaflet still owns markers, clusters,
 * zoom, pan, and popups. Raster PNG tiles (light_all) are only the fallback.
 *
 * Why vector: Carto counts each raster tile as a request. Image tiles were
 * exceeding the commercial free cap (~1M/month). Vector style + tiles + sprites
 * + glyphs are far fewer requests for the same Positron look.
 *
 * Why CDN, not npm: Laravel Mix 5 / Webpack 4 / Node 13 cannot bundle modern
 * MapLibre. window.L = L is required because Leaflet is a webpack module and
 * the CDN plugin expects a global.
 *
 * Pin MapLibre 2.2.1 + maplibre-gl-leaflet 0.0.20. Newer pairs have known
 * Leaflet drag/zoom sync bugs.
 *
 * Auth: CARTO_API_KEY (see .env) is exposed as AKTIALKV.cartoApiKey. Carto
 * vector/CORS needs `api_key=`, not `key=`. transformRequest must add it to
 * every cartocdn.com URL (style, TileJSON, sprites, fonts, .mvt).
 *
 * Request cuts besides vector:
 * - Fit the marker view before adding the basemap so Finland zoom 6 is not
 *   fetched and thrown away.
 * - Lazy-init #apartment-map (apartment / office / broker) when it scrolls
 *   or its tab is shown. Listing and offices-landing hero maps stay eager.
 * - Cap maxZoom at 15 on listing/office overviews; apartment maps stay at 16.
 */
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import map from 'lodash/map';
import each from 'lodash/each';
import filter from 'lodash/filter';
import axios from 'axios';

const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';
const CARTO_POSITRON_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@2.2.1/dist/maplibre-gl.css';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@2.2.1/dist/maplibre-gl.js';
const MAPLIBRE_LEAFLET_JS = 'https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.20/leaflet-maplibre-gl.js';
const FINLAND_CENTER = [61.9241, 25.7482];
const FINLAND_ZOOM = 6;
const MAP_PRESETS = {
    map: { maxZoom: 15, lazy: false },
    'offices-map': { maxZoom: 15, lazy: false },
    'apartment-map': { maxZoom: 16, lazy: true }
};

let vectorLibsPromise = null;

function cartoApiKey() {
    return (typeof AKTIALKV !== 'undefined' && AKTIALKV.cartoApiKey)
        ? AKTIALKV.cartoApiKey
        : '';
}

function withCartoKey(url, key) {
    if (!key || url.indexOf('cartocdn.com') === -1 || url.indexOf('api_key=') !== -1) {
        return url;
    }

    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'api_key=' + encodeURIComponent(key);
}

function loadStylesheet(href) {
    if (document.querySelector('link[href="' + href + '"]')) {
        return Promise.resolve();
    }

    return new Promise(function(resolve, reject) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = resolve;
        link.onerror = reject;
        document.head.appendChild(link);
    });
}

function loadScript(src) {
    if (document.querySelector('script[src="' + src + '"]')) {
        return Promise.resolve();
    }

    return new Promise(function(resolve, reject) {
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

function loadVectorBasemapLibs() {
    if (window.maplibregl && L.maplibreGL) {
        return Promise.resolve(true);
    }

    if (!vectorLibsPromise) {
        window.L = L;
        vectorLibsPromise = loadStylesheet(MAPLIBRE_CSS)
            .then(function() { return loadScript(MAPLIBRE_JS); })
            .then(function() { return loadScript(MAPLIBRE_LEAFLET_JS); })
            .then(function() { return true; })
            .catch(function(error) {
                console.error('Could not load Carto vector basemap libraries:', error);
                return false;
            });
    }

    return vectorLibsPromise;
}

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create a map of status to icon names
const markerIcons = {
    default: 'icon-marker-default',
    sold: 'icon-marker-sold',
    upcoming: 'icon-marker-upcoming',
    viewing: 'icon-marker-viewing'
};

export default class LeafletMap {
    constructor(elementId, markers = []) {
        this.map = null;
        this.markers = markers;
        this.popups = {};
        this.elementId = elementId;
        this.mapElement = null;
        this.initialized = false;
        this.preset = MAP_PRESETS[elementId] || MAP_PRESETS.map;
        this.maxZoom = this.preset.maxZoom;

        // Wait for DOM to be ready and ensure the element exists
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initialize());
        } else {
            // Small delay to ensure the element is in the DOM
            setTimeout(() => this.initialize(), 0);
        }
    }

    initialize() {
        this.mapElement = document.getElementById(this.elementId);
        if (!this.mapElement) {
            console.warn(`Map container #${this.elementId} not found`);
            return;
        }

        if (this.initialized) {
            return;
        }

        if (this.preset.lazy) {
            this.whenVisible(() => this.startMap());
            return;
        }

        this.startMap();
    }

    whenVisible(callback) {
        if (!('IntersectionObserver' in window)) {
            callback();
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry || !entry.isIntersecting || entry.intersectionRect.height < 1) {
                return;
            }

            observer.disconnect();

            // Apartment "Show on map" pane animates from height 0
            if (this.mapElement.offsetHeight < 100) {
                setTimeout(callback, 250);
            } else {
                callback();
            }
        }, { rootMargin: '200px 0px', threshold: 0 });

        observer.observe(this.mapElement);
    }

    startMap() {
        if (this.initialized || !this.mapElement) {
            return;
        }

        this.ensureMapSize();
        this.initialized = true;

        loadVectorBasemapLibs().then((vectorReady) => {
            this.initMap();
            this.setMarkers();
            this.addCartoBasemap(vectorReady);

            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize();
                }
            }, 100);
        });
    }

    ensureMapSize() {
        const style = window.getComputedStyle(this.mapElement);
        if (style.height === '0px' || style.height === 'auto') {
            this.mapElement.style.height = '400px';
        }
        if (style.width === '0px' || style.width === 'auto') {
            this.mapElement.style.width = '100%';
        }
        if (style.display === 'none') {
            this.mapElement.style.display = 'block';
        }
    }

    addCartoBasemap(useVector) {
        const key = cartoApiKey();

        if (useVector && L.maplibreGL) {
            L.maplibreGL({
                style: withCartoKey(CARTO_POSITRON_STYLE, key),
                attribution: CARTO_ATTRIBUTION,
                transformRequest: function(url) {
                    return { url: withCartoKey(url, key) };
                }
            }).addTo(this.map);
            return;
        }

        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?api_key={apikey}', {
            attribution: CARTO_ATTRIBUTION,
            subdomains: 'abcd',
            maxZoom: this.maxZoom,
            apikey: key
        }).addTo(this.map);
    }

    initMap() {
        if (!this.mapElement) return;

        try {
            // View is set in setMarkers before the basemap is added, so Finland
            // zoom 6 is not fetched and discarded.
            this.map = L.map(this.mapElement, {
                zoomControl: false,
                maxZoom: this.maxZoom,
                scrollWheelZoom: false,
                attributionControl: false
            });

            L.control.zoom({
                position: 'bottomright'
            }).addTo(this.map);

            L.control.attribution({
                position: 'bottomright',
                prefix: false
            }).addTo(this.map);
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    setMarkers() {
        // Filter markers without latitude and longitude
        this.markers = filter(this.markers, (marker) => {
            return (marker.lat && marker.lng);
        });

        // Create a marker cluster group
        const clusterGroup = L.markerClusterGroup({
            maxClusterRadius: 50,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            disableClusteringAtZoom: Math.min(14, this.maxZoom)
        });

        // Convert markers to Leaflet markers
        this.markers = map(this.markers, (marker, i) => {
            // Use the provided SVG as the marker icon
            const customIcon = L.divIcon({
                className: 'leaflet-marker-icon-custom',
                html: `<svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 31.1668C22.6666 25.5002 28.3333 20.4261 28.3333 14.1668C28.3333 7.9076 23.2592 2.8335 17 2.8335C10.7407 2.8335 5.66663 7.9076 5.66663 14.1668C5.66663 20.4261 11.3333 25.5002 17 31.1668Z" fill="#191919"/><path d="M17 18.4165C19.3472 18.4165 21.25 16.5137 21.25 14.1665C21.25 11.8193 19.3472 9.9165 17 9.9165C14.6528 9.9165 12.75 11.8193 12.75 14.1665C12.75 16.5137 14.6528 18.4165 17 18.4165Z" fill="white"/></svg>`,
                iconSize: [34, 34],
                iconAnchor: [17, 34],
                popupAnchor: [0, -80]
            });

            const mapMarker = L.marker([parseFloat(marker.lat), parseFloat(marker.lng)], {
                icon: customIcon
            });

            // Store the original marker data in the Leaflet marker
            mapMarker.markerData = marker;

            // Create popup immediately for both office and apartment markers
            const popup = L.popup({
                className: 'leaflet-popup-custom',
                maxWidth: 300,
                closeButton: true,
                offset: marker.info ? [0, -40] : [0, 0]  // Different offset for office markers
            });

            if (marker.info) {
                // For office markers, set content immediately
                const content = this.popupContent({
                    url: marker.info.url,
                    title: marker.info.title,
                    desc: marker.info.desc
                });
                popup.setContent(content);

                // Add custom class for office popups
                popup.options.className = 'leaflet-popup-custom leaflet-popup-office';
            } else {
                // For apartment markers, set loading content
                popup.setContent('Loading...');
            }

            // Bind popup to marker and store in cache
            mapMarker.bindPopup(popup);
            this.popups[marker.id] = popup;

            // For apartment markers, fetch data on first click
            if (!marker.info) {
                mapMarker.on('click', () => {
                    // Only fetch data if we haven't already
                    if (popup.getContent() === 'Loading...') {
                        this.fetchApartmentData(marker.id, popup);
                    }
                });
            }

            // Keep popups in cache when they're closed
            mapMarker.on('popupclose', () => {
                console.log('Popup closed for marker:', marker.id);
            });

            clusterGroup.addLayer(mapMarker);
            return mapMarker;
        });

        // Add the cluster group to the map
        this.map.addLayer(clusterGroup);

        if (this.markers.length > 0) {
            const bounds = L.latLngBounds(this.markers.map(marker => marker.getLatLng()));
            this.map.fitBounds(bounds, {
                padding: [50, 50],
                animate: false,
                maxZoom: this.maxZoom
            });
        } else {
            this.map.setView(FINLAND_CENTER, Math.min(FINLAND_ZOOM, this.maxZoom));
        }
    }

    // Separate method for fetching apartment data
    fetchApartmentData(itemId, popup) {
        axios.get('/api/apartment/' + itemId, { baseURL: AKTIALKV.siteUrl })
            .then((response) => {
                const desc = `
                    <div class="apartment-card-text leaflet-popup__properties">
                        ${response.data.price ? '<p>' + response.data.price + '</p>' : ''}
                        <p>${response.data.propertySize}</p>
                        ${response.data.roomCount ? '<p>' + response.data.roomCount + '</p>' : ''}
                        ${response.data.presentationStartDate ? '<p>' + response.data.presentationStartDate + '</p>' : ''}
                    </div>
                    ${this.apartmentStatus(response.data)}
                `;

                const info = {
                    url: response.data.url,
                    title: response.data.title,
                    desc: desc
                };

                popup.setContent(this.popupContent(info));
            })
            .catch(error => {
                console.error('Error fetching apartment data:', error);
                popup.setContent('Error loading apartment information');
            });
    }

    popupContent(data) {
        return `
            <a href="${data.url}" class="leaflet-popup__info">
                <h2 class="leaflet-popup__title">${data.title}</h2>
                ${data.desc}
            </a>
        `;
    }

    apartmentStatus(data) {
        return (data.status != 'default' && data.status != 'viewing') ?
            `<p class="leaflet-popup__status">${data.statusText}</p>` :
            '';
    }
} 